import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import useBoolean from 'src/hooks/useBoolean';
import useConfirm from 'src/hooks/useConfirm';

import useAttachToReceiptOrInvoiceApi from 'src/services/useAttachToReceiptOrInvoiceApi';
import useDeleteDocumentApi from 'src/services/useDeleteDocumentApi';
import { InvoiceAttachment, InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import useUnattachDocumentsApi from 'src/services/useUnattachDocumentsApi';
import { IReceipt as ReceiptDetailsResponse } from 'src/types/Receipt';

const useScansActions = (
  attachment: InvoiceAttachment | undefined,
  currentDocumentIndex: number,
  decrementCurrentDocumentIndex: VoidFunction,
  isInvoice: boolean,
  id: string,
  document: any,
  getDocument: () => Promise<InvoiceDetailsResponse | undefined> | Promise<ReceiptDetailsResponse>,
  isPoOpen: boolean,
  isProcessingComplete: boolean
) => {
  const { confirm } = useConfirm();
  const { deleteAttachment, isLoading: isDeleting } = useDeleteDocumentApi(Number(id), isInvoice);
  const { unAttachDocument, isLoading: isUnAttaching } = useUnattachDocumentsApi(isInvoice);

  const {
    attachDocumentsToReceiptOrInvoice: attachDocuments,
    isAttachDocumentLoading: isAttaching,
    status: attachDocApiStatus,
  } = useAttachToReceiptOrInvoiceApi(isInvoice);

  const {
    value: isUploadScansOpen,
    setTrue: openUploadScans,
    setFalse: closeUploadScans,
  } = useBoolean();

  const {
    value: isAttachExistingDocumentsOpen,
    setTrue: openAttachExistingDocuments,
    setFalse: closeAttachExistingDocuments,
  } = useBoolean();

  const deleteCurrentDocument = useCallback(async () => {
    const isDeleteAccepted = await confirm({
      title: 'Delete Document',
      content: <div>Are you sure you want to delete the document?</div>,
      confirmButtonText: 'Delete',
    });
    if (isDeleteAccepted) {
      deleteAttachment({ ids: [attachment?.attachId as number] }).then(() => {
        toast.success('Attachment deleted successfully!');
        if (document?.attachments?.length === currentDocumentIndex + 1) {
          decrementCurrentDocumentIndex();
        }
        getDocument();
      });
    }
  }, [
    attachment?.attachId,
    confirm,
    currentDocumentIndex,
    decrementCurrentDocumentIndex,
    deleteAttachment,
    document?.attachments?.length,
    getDocument,
  ]);

  const unAttachCurrentDocument = useCallback(() => {
    unAttachDocument({ id: parseInt(id), attachId: attachment?.attachId as number }).then(() => {
      toast.success('Attachment unattached successfully!');
      if (document?.attachments?.length === currentDocumentIndex + 1) {
        decrementCurrentDocumentIndex();
      }
      getDocument();
    });
  }, [
    attachment?.attachId,
    currentDocumentIndex,
    decrementCurrentDocumentIndex,
    document?.attachments?.length,
    getDocument,
    id,
    unAttachDocument,
  ]);

  const [isDeleteDisabled, deleteDisableMessage] = useMemo(
    () =>
      [
        !attachment || !isPoOpen || isProcessingComplete,
        !isPoOpen
          ? ['Current Document PO Status is closed']
          : !attachment
          ? ['Upload or Attach to Delete an Attachment']
          : isProcessingComplete
          ? ['Current Document is already Processed.']
          : [],
      ] as const,
    [attachment, isPoOpen, isProcessingComplete]
  );
  const [isUnAttachDisabled, unAttachDisableMessage] = useMemo(
    () =>
      [
        !attachment || !isPoOpen || isProcessingComplete,
        !isPoOpen
          ? ['Current Document PO Status is closed']
          : !attachment
          ? ['Upload or Attach to UnAttach an Attachment']
          : isProcessingComplete
          ? ['Current Document is already Processed.']
          : [],
      ] as const,
    [attachment, isPoOpen, isProcessingComplete]
  );

  const [isUploadDisabled, uploadMessage] = useMemo(
    () =>
      [
        !isPoOpen || isProcessingComplete,
        !isPoOpen
          ? ['Current Document PO Status is closed']
          : isProcessingComplete
          ? ['Current Document is already Processed.']
          : [],
      ] as const,
    [isPoOpen, isProcessingComplete]
  );
  const [isAttachDisabled, attachMessage] = useMemo(
    () =>
      [
        !isPoOpen || isProcessingComplete,
        !isPoOpen
          ? ['Current Document PO Status is closed']
          : isProcessingComplete
          ? ['Current Document is already Processed.']
          : [],
      ] as const,
    [isPoOpen, isProcessingComplete]
  );

  return {
    deleteCurrentDocument,
    isDeleting,
    isDeleteDisabled,
    isUnAttachDisabled,
    isUnAttaching,
    unAttachCurrentDocument,
    isUploadScansOpen,
    openUploadScans,
    closeUploadScans,
    isUploadDisabled,
    isAttachDisabled,
    isAttachExistingDocumentsOpen,
    openAttachExistingDocuments,
    closeAttachExistingDocuments,
    attachDocuments,
    isAttaching,
    attachDocApiStatus,
    deleteDisableMessage,
    unAttachDisableMessage,
    uploadMessage,
    attachMessage,
  } as const;
};

export default useScansActions;
